import React, { useContext, useState, useEffect, useMemo } from "react";
import {
  Home,
  Users,
  ImagePlus,
  Tag,
  IdCard,
  Store,
  Package,
  ShoppingCart,
  ArrowLeftRight,
  CreditCard,
  CircleHelp,
  Headphones,
  Settings,
  Settings2,
  PanelLeftClose,
  PanelLeftOpen,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  Sparkles,
  Truck,
  PlusSquare,
  Wallet,
  Mail,
} from "lucide-react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import Cookies from "js-cookie";
import menuData from "./Dashboard/menuData.json";
import { AuthContext } from "../context/Context";
import { cn } from "../lib/cn";

const iconMap = {
  HomeOutlined: Home,
  UserOutlined: Users,
  AppstoreAddOutlined: ImagePlus,
  TagOutlined: Tag,
  ProfileOutlined: IdCard,
  ShopOutlined: Store,
  AppstoreOutlined: Package,
  ShoppingCartOutlined: ShoppingCart,
  TransactionOutlined: ArrowLeftRight,
  CreditCardOutlined: CreditCard,
  QuestionCircleOutlined: CircleHelp,
  CustomerServiceOutlined: Headphones,
  SettingOutlined: Settings,
  Settings2Outlined: Settings2,
  TruckOutlined: Truck,
  PlusSquareOutlined: PlusSquare,
  WalletOutlined: Wallet,
  MailOutlined: Mail,
};

function normalizePath(p) {
  if (!p) return "/";
  const t = p.replace(/\/+$/, "");
  return t === "" ? "/" : t;
}

function pathMatches(pathname, route) {
  const p = normalizePath(pathname);
  const r = normalizePath(route);
  if (p === r) return true;
  if (r !== "/" && p.startsWith(r + "/")) return true;
  return false;
}

function NavBadgeDot({ show }) {
  if (!show) return null;
  return (
    <span className="ml-auto inline-flex h-2 w-2 shrink-0 rounded-full bg-admin-accent shadow-[0_0_10px_rgba(167,139,250,0.9)]" />
  );
}

const DashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { getAllApi } = useContext(AuthContext);

  const initialOpen = useMemo(() => {
    const o = {};
    menuData.forEach((item) => {
      if (
        item.children?.some((c) => pathMatches(location.pathname, c.route))
      ) {
        o[item.route] = true;
      }
    });
    return o;
  }, [location.pathname]);

  const [openGroups, setOpenGroups] = useState(initialOpen);

  useEffect(() => {
    setOpenGroups((prev) => {
      const next = { ...prev };
      menuData.forEach((item) => {
        if (
          item.children?.some((c) => pathMatches(location.pathname, c.route))
        ) {
          next[item.route] = true;
        }
      });
      return next;
    });
  }, [location.pathname]);

  useEffect(() => {
    getAllApi();
    const authToken = Cookies.get("authToken");
    if (!authToken) navigate("/login");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const showNewLabel = (item) =>
    item.badge === "new" ||
    item.label === "Coupons" ||
    item.label === "Products" ||
    item.label === "Create order";

  return (
    <div className="min-h-screen bg-slate-100 font-sans text-slate-900">
      {/* Top bar */}
      <header className="fixed inset-x-0 top-0 z-50 flex h-14 items-center gap-3 border-b border-violet-200/70 bg-white/90 px-3 shadow-sm backdrop-blur-md md:px-5">
        <button
          type="button"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:border-brand/30 hover:bg-brand-surface md:hidden"
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5" strokeWidth={2} />
        </button>

        <button
          type="button"
          className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-600 transition hover:bg-brand-surface hover:text-brand md:flex"
          onClick={() => setCollapsed((c) => !c)}
          aria-label="Toggle sidebar width"
        >
          {collapsed ? (
            <PanelLeftOpen className="h-5 w-5" strokeWidth={2} />
          ) : (
            <PanelLeftClose className="h-5 w-5" strokeWidth={2} />
          )}
        </button>

        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand to-brand-dark text-white shadow-md shadow-brand/25">
              <Sparkles className="h-[18px] w-[18px]" strokeWidth={2} />
            </div>
            <div className="min-w-0 leading-tight">
              <p className="truncate text-sm font-bold tracking-tight text-slate-900">
                Tidyhub
              </p>
              <p className="truncate text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Admin console
              </p>
            </div>
          </div>
        </div>

        <div className="hidden items-center gap-2 sm:flex">
          <span className="rounded-full border border-slate-200/80 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">
            {new Date().toLocaleDateString(undefined, {
              weekday: "short",
              month: "short",
              day: "numeric",
            })}
          </span>
        </div>
      </header>

      {/* Desktop sidebar */}
      <aside
        className={cn(
          collapsed ? "w-[76px]" : "w-64",
          "fixed bottom-0 left-0 top-14 z-40 hidden overflow-hidden border-r border-admin-border bg-admin-sidebar bg-gradient-to-b from-[#0f172a] via-[#1e1b4b] to-[#172554] shadow-shell transition-[width] duration-200 ease-out md:block"
        )}
      >
        <div className="flex h-full flex-col">
          <div className="border-b border-white/10 px-3 py-4">
            {!collapsed && (
              <p className="px-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/40">
                Navigation
              </p>
            )}
          </div>
          <nav className="flex-1 space-y-0.5 overflow-y-auto overscroll-contain px-2 py-3">
            {menuData.map((item) => {
              const IconCmp = iconMap[item.icon];
              if (!IconCmp) return null;

              if (item.children?.length) {
                const expanded = !!openGroups[item.route];
                const childActive = item.children.some((c) =>
                  pathMatches(location.pathname, c.route)
                );
                return (
                  <div key={item.key ?? item.route} className="mb-1">
                    <button
                      type="button"
                      onClick={() =>
                        setOpenGroups((s) => ({
                          ...s,
                          [item.route]: !s[item.route],
                        }))
                      }
                      className={cn(
                        "group flex w-full items-center gap-2.5 rounded-md px-2.5 py-2.5 text-left text-sm transition",
                        childActive
                          ? "bg-admin-accentSoft/50 text-admin-accent shadow-inner ring-1 ring-inset ring-admin-accent/20"
                          : "text-slate-300 hover:bg-white/5 hover:text-white"
                      )}
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-white/5 text-white/90 ring-1 ring-white/10">
                        <IconCmp className="h-[18px] w-[18px]" strokeWidth={1.75} />
                      </span>
                      {!collapsed && (
                        <>
                          <span className="min-w-0 flex-1 font-medium">
                            {item.label}
                          </span>
                          <NavBadgeDot show={showNewLabel(item)} />
                          {expanded ? (
                            <ChevronDown className="h-4 w-4 shrink-0 text-white/50" />
                          ) : (
                            <ChevronRight className="h-4 w-4 shrink-0 text-white/50" />
                          )}
                        </>
                      )}
                    </button>
                    {expanded && !collapsed && (
                      <div className="mt-1 ml-2 space-y-0.5 border-l border-white/10 pl-2.5">
                        {item.children.map((sub) => {
                          const sel = pathMatches(location.pathname, sub.route);
                          return (
                            <button
                              key={sub.key ?? sub.route}
                              type="button"
                              onClick={() => navigate(sub.route)}
                              className={cn(
                                "block w-full rounded-md py-1.5 pl-2.5 pr-2 text-left text-[13px] transition",
                                sel
                                  ? "bg-admin-accentSoft font-semibold text-admin-accent ring-1 ring-inset ring-admin-accent/25"
                                  : "text-slate-400 hover:bg-white/5 hover:text-white"
                              )}
                            >
                              {sub.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              const sel = pathMatches(location.pathname, item.route);
              return (
                <button
                  key={item.key ?? item.route}
                  type="button"
                  onClick={() => navigate(item.route)}
                  className={cn(
                    "mb-0.5 flex w-full items-center gap-2.5 rounded-md px-2.5 py-2.5 text-left text-sm transition",
                    sel
                      ? "bg-admin-accentSoft text-admin-accent ring-1 ring-inset ring-admin-accent/25"
                      : "text-slate-300 hover:bg-white/5 hover:text-white"
                  )}
                >
                  <span
                    className={cn(
                      "flex h-9 w-9 shrink-0 items-center justify-center rounded-md ring-1",
                      sel
                        ? "bg-brand-deeper/50 text-admin-accent ring-admin-accent/35"
                        : "bg-white/5 text-white/90 ring-white/10"
                    )}
                  >
                    <IconCmp className="h-[18px] w-[18px]" strokeWidth={1.75} />
                  </span>
                  {!collapsed && (
                    <>
                      <span className="min-w-0 flex-1 font-medium">{item.label}</span>
                      <NavBadgeDot show={showNewLabel(item)} />
                    </>
                  )}
                </button>
              );
            })}
          </nav>
          {!collapsed && (
            <div className="border-t border-white/10 p-4 text-[11px] leading-relaxed text-slate-500">
              Signed in as admin. All routes above stay in sync with your app.
            </div>
          )}
        </div>
      </aside>

      {/* Mobile drawer */}
      <div
        className={cn(
          "fixed inset-0 z-[60] md:hidden",
          mobileOpen ? "pointer-events-auto" : "pointer-events-none"
        )}
        aria-hidden={!mobileOpen}
      >
        <button
          type="button"
          className={cn(
            "absolute inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity",
            mobileOpen ? "opacity-100" : "opacity-0"
          )}
          onClick={() => setMobileOpen(false)}
          aria-label="Close menu"
        />
        <div
          className={cn(
            "absolute left-0 top-0 flex h-full w-[min(100vw-3rem,288px)] max-w-full flex-col bg-admin-sidebar shadow-2xl transition-transform duration-200 ease-out",
            mobileOpen ? "translate-x-0" : "-translate-x-full"
          )}
        >
          <div className="flex items-center justify-between border-b border-white/10 px-4 py-4">
            <span className="text-sm font-bold text-white">Menu</span>
            <button
              type="button"
              className="rounded-lg p-2 text-white/70 hover:bg-white/10 hover:text-white"
              onClick={() => setMobileOpen(false)}
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto px-2 py-3">
            {menuData.map((item) => {
              const IconCmp = iconMap[item.icon];
              if (!IconCmp) return null;
              if (item.children?.length) {
                return (
                  <div key={item.key ?? item.route} className="mb-3">
                    <div className="flex items-center gap-2 px-2 py-2 text-xs font-bold uppercase tracking-wider text-white/40">
                      <IconCmp className="h-4 w-4" />
                      {item.label}
                    </div>
                    {item.children.map((sub) => {
                      const subActive = pathMatches(location.pathname, sub.route);
                      return (
                        <button
                          key={sub.route}
                          type="button"
                          onClick={() => navigate(sub.route)}
                          className={cn(
                            "block w-full rounded-md px-3 py-2 text-left text-sm transition",
                            subActive
                              ? "bg-admin-accentSoft font-semibold text-admin-accent ring-1 ring-inset ring-admin-accent/25"
                              : "text-slate-200 hover:bg-white/10 hover:text-white"
                          )}
                        >
                          {sub.label}
                        </button>
                      );
                    })}
                  </div>
                );
              }
              const navActive = pathMatches(location.pathname, item.route);
              return (
                <button
                  key={item.key ?? item.route}
                  type="button"
                  onClick={() => navigate(item.route)}
                  className={cn(
                    "mb-1 flex w-full items-center gap-3 rounded-md px-3 py-3 text-left text-sm font-medium transition",
                    navActive
                      ? "bg-admin-accentSoft text-admin-accent ring-1 ring-inset ring-admin-accent/25"
                      : "text-white hover:bg-white/10"
                  )}
                >
                  <IconCmp className="h-5 w-5 shrink-0" />
                  {item.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      <main
        className={cn(
          "admin-page min-h-[calc(100vh-3.5rem)] overflow-x-auto px-3 pb-16 pt-[calc(3.5rem+1.25rem)] transition-[margin] duration-200 md:px-8",
          collapsed ? "md:ml-[76px]" : "md:ml-64"
        )}
      >
        <div className="mx-auto max-w-[1600px]">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
